// Library Methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  ResponsiveContainer,
  Customized,
  Text,
  ReferenceLine,
  ReferenceArea,
} from "recharts";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import RAPDChartLegend from "./RAPDChartLegend/RAPDChartLegend";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

type RAPDChartType = {
  chartData:
    | ExamSectionReportType["postProcessing"]
    | ExamSectionReportType["state"];
  chartHeight?: number;
  testDuration?: number;
  isLiveUpdate?: boolean;
  displayTitle?: boolean;
};

const RAPDChart = ({
  chartData,
  chartHeight,
  testDuration = 20,
  isLiveUpdate = false,
  displayTitle = true,
}: RAPDChartType) => {
  const { t } = useTranslation();

  // Live update renders inside a height-constrained modal panel, so it needs
  // a shorter chart than the full report page's default.
  const resolvedChartHeight = chartHeight ?? (isLiveUpdate ? 220 : 400);

  const theme = useTheme();
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const hasNoEntries = useMemo(
    () => chartData?.pupilEventData?.length === 0,
    [chartData?.pupilEventData?.length]
  );

  // Convert a date string to UTC milliseconds
  const convertDateToMs = useCallback(
    (utcString: string) =>
      DateTime.fromISO(utcString, { zone: "utc" }).toMillis(),
    []
  );

  // First event time in UTC milliseconds
  const startingTimeMs = useMemo(() => {
    if (chartData?.pupilEventData?.[0]?.date)
      return convertDateToMs(chartData?.pupilEventData?.[0]?.date);

    return 0;
  }, [chartData?.pupilEventData, convertDateToMs]);

  // Format all pupil events to have a startDate in milliseconds
  // (Measurement time starts at 0)
  const formattedPupilEvents = useMemo(
    () =>
      chartData?.pupilEventData?.map((pupilData) => ({
        ...pupilData,
        startDate: convertDateToMs(pupilData?.date) - startingTimeMs,
      })),
    [chartData?.pupilEventData, startingTimeMs, convertDateToMs]
  );

  // Format all DB update events to have a startDate in milliseconds
  // (Measurement time starts at 0)
  const formattedDBEvents = useMemo(
    () =>
      chartData?.fieldDBUpdatedEvents?.map((updatedEvent) => ({
        ...updatedEvent,
        startDate: convertDateToMs(updatedEvent?.startDate) - startingTimeMs,
      })),
    [chartData?.fieldDBUpdatedEvents, startingTimeMs, convertDateToMs]
  );

  // Format all blink events to have a startDate in milliseconds
  // (Measurement time starts at 0)
  const formattedBlinkEvents = useMemo(
    () =>
      chartData?.blinkEvents?.map((blinkEvent) => ({
        ...blinkEvent,
        startDate: convertDateToMs(blinkEvent?.startDate) - startingTimeMs,
        endDate: convertDateToMs(blinkEvent?.endDate) - startingTimeMs,
      })),
    [chartData?.blinkEvents, startingTimeMs, convertDateToMs]
  );

  // Formatted x axis ticks in milliseconds
  const chartTicks = useMemo(
    () =>
      Array.from({ length: Math.round(testDuration) + 1 }).map(
        (_, i) => i * 1000
      ),
    [testDuration]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        gap: "20px",
      }}
    >
      {displayTitle && (
        <Typography fontSize="14px" fontWeight={600}>
          {t("Pupil Diameter Over Time")}
        </Typography>
      )}
      <RAPDChartLegend />
      <div
        style={{
          width: "100%",
          display: "flex",
        }}
      >
        <ResponsiveContainer width="100%" height={resolvedChartHeight}>
          <LineChart
            width={500}
            height={200}
            data={formattedPupilEvents}
            syncId="pupilDataId"
            margin={{
              top: 30,
              right: 15,
              left: hasNoEntries ? -45 : 5,
              bottom: hasNoEntries ? 0 : 30,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="startDate"
              textAnchor="end"
              angle={-40}
              type="number"
              scale="time"
              domain={["auto", "auto"]}
              interval={isLiveUpdate ? 3 : undefined}
              ticks={isLiveUpdate ? undefined : chartTicks}
              fontSize={12}
              tickFormatter={(value) => Math.round(value / 1000).toString()} // display time in seconds
            >
              <Label
                value={`${t("Time")} (s)`}
                position="insideBottom"
                dx={isLiveUpdate ? -25 : 0}
                dy={30}
                style={{ fontSize: "14px" }}
              />
            </XAxis>
            <YAxis
              fontSize={12}
              tickFormatter={(value) => value.toFixed(1)}
              allowDecimals={true}
              type="number"
            >
              <Label
                value={`${t("Pupil Diameter")} (mm)`}
                position="insideLeft"
                angle={-90}
                style={{ textAnchor: "middle", fontSize: "14px" }}
              />
            </YAxis>
            <Customized
              component={() => {
                return hasNoEntries ? (
                  <Text
                    style={{
                      fontSize: "14px",
                      transform: `translate(50%, 50%)`,
                      textAnchor: "middle",
                      verticalAlign: "middle",
                    }}
                  >
                    {t("No Data Available.")}
                  </Text>
                ) : null;
              }}
            />
            <Line
              type="monotone"
              dataKey="pupilDiameterLeft"
              stroke="red"
              fill="red"
              dot={<></>}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="pupilDiameterRight"
              stroke="blue"
              fill="blue"
              dot={<></>}
              isAnimationActive={false}
            />

            {formattedDBEvents?.map((updateEvent, index: number) => (
              <ReferenceLine
                key={`update-event-${index}`}
                x={updateEvent.startDate}
                stroke="black"
              >
                {updateEvent.updatedLeftDb !== null && (
                  <Label
                    value={`${updateEvent.updatedLeftDb} dB`}
                    position="top"
                    fill={smallerThanSm ? "#FFF" : "red"}
                    fontSize={10}
                    dy={-13}
                  />
                )}
                {updateEvent.updatedRightDb !== null && (
                  <Label
                    value={`${updateEvent.updatedRightDb} dB`}
                    position="top"
                    fill={smallerThanSm ? "#FFF" : "blue"}
                    fontSize={10}
                    dy={0}
                  />
                )}
              </ReferenceLine>
            ))}

            {formattedBlinkEvents?.map((blinkEvent, index: number) => (
              <ReferenceArea
                key={`blink-event-${index}`}
                x1={blinkEvent.startDate}
                x2={blinkEvent.endDate}
                stroke="green"
                fill="green"
                opacity={0.4}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Box>
  );
};

export default RAPDChart;
